import styled from '@emotion/styled'
import React, { Children, cloneElement, Fragment, isValidElement, memo } from 'react'

import { isFragment } from '../../code-only/util.ts'
import { borderRadius, borderStroke, colorTokens, spacing } from '../../foundations/index.ts'
import { Divider } from '../divider/divider.tsx'

import type { RowProps } from './row.tsx'

/** Do not use variant="dropdown" directly
 * Use RowContainer as Dropdown children
 * Dropdown sets variant="dropdown" internally */
export type RowContainerVariant = 'simple' | 'border' | 'spacing' | 'dropdown'

export type RowContainerProps = {
  variant: RowContainerVariant
  size: RowProps['size']
  checkbox?: boolean
  radio?: boolean
  children: React.ReactNode
  horizontal?: boolean
  className?: string | undefined
}

export const RowContainer = memo<RowContainerProps>(function RowContainer(props) {
  const { variant, size, checkbox, radio, children, horizontal = false, className } = props

  // Children are assumed to be Rows: nothing checks it at runtime.
  const filteredChildren = Children.toArray(children).filter(
    (child): child is React.ReactElement<RowProps> => isValidElement<RowProps>(child) && !isFragment(child),
  )

  return (
    <RowContainerStyle variant={variant} className={className} horizontal={horizontal}>
      {filteredChildren.map((child, index) => (
        <Fragment key={index}>
          {variant !== 'spacing' && variant !== 'dropdown' && index > 0 ? (
            <Divider orientation="horizontal" className="divider" />
          ) : null}
          {cloneElement(child, {
            variant: variant === 'spacing' ? 'standalone' : variant === 'dropdown' ? 'dropdown' : child.props.variant,
            size,
            // A row's own value wins, so a checkbox list can hold a row that is shown but cannot be selected.
            checkbox: child.props.checkbox ?? checkbox,
            radio,
            disableIndividualBorderRadius: variant === 'border',
          })}
        </Fragment>
      ))}
    </RowContainerStyle>
  )
})

export const RowContainerStyle = styled.div<{ variant: RowContainerVariant; horizontal: boolean }>`
  display: flex;
  flex-flow: ${({ horizontal }) => (horizontal ? 'row wrap' : 'column wrap')};
  gap: ${({ variant }) => (variant === 'spacing' ? spacing[30] : '0')};

  & > *:not(.divider) {
    padding: ${({ variant }) => (variant === 'border' ? `${spacing[40]} ${spacing[50]}` : 'none')};
  }

  border: ${({ variant }) =>
    variant === 'border' ? `${borderStroke['20']} solid ${colorTokens['color-stroke-base-main']}` : 'none'};
  border-radius: ${({ variant }) => (variant === 'border' ? `${borderRadius['20']}` : '0')};

  & > *:not(.divider):first-of-type {
    border-radius: ${({ variant }) => (variant === 'border' ? `${borderRadius['20']} ${borderRadius['20']} 0 0` : '')};
  }

  & > *:not(.divider):last-of-type {
    border-radius: ${({ variant }) => (variant === 'border' ? `0 0 ${borderRadius['20']} ${borderRadius['20']}` : '')};
  }

  & > *:not(.divider):first-of-type:last-of-type {
    border-radius: ${({ variant }) => (variant === 'border' ? `${borderRadius['20']}` : '')};
  }
`
